import {toast} from '@wandb/weave/common/components/elements/Toast';
import {hexToRGB, MOON_950} from '@wandb/weave/common/css/globals.styles';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';

import {
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner';
import * as S from './AddVersionHelpBanner.styles';

function getLogModelCodeSnippet(artifactTypeName: string) {
  return `
  # create a run and log a ${artifactTypeName}artifact to it
  with wandb.init(project="${artifactTypeName}-registration-example") as run:
    ${artifactTypeName}_artifact = wandb.Artifact(
      name="my_${artifactTypeName}", 
      type="${artifactTypeName}"
    )
    ${artifactTypeName}_artifact.add_file("${artifactTypeName}_checkpt.pt")
    run.log_artifact(${artifactTypeName}_artifact)
    # logs the ${artifactTypeName} version "my_${artifactTypeName}:v0"
`;
}

type AddVersionHelpBannerNoLoggedVersionsProps = {
  artifactTypeName: string;
};

export const AddVersionHelpBannerNoLoggedVersions = ({
  artifactTypeName,
}: AddVersionHelpBannerNoLoggedVersionsProps) => {
  const [isLogArtifactCodeExpanded, setIsLogArtifactCodeExpanded] =
    useState(false);
  const onToggleArtifactModelCodeExpanded = () => {
    setIsLogArtifactCodeExpanded(!isLogArtifactCodeExpanded);
  };

  const onCopyLogArtifactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(getLogModelCodeSnippet(artifactTypeName));
    toast('Copied to clipboard');
  };
  return (
    <>
      <S.BannerWithMargin variant="info">
        <BannerFlexWrapper>
          <Icon name="info" style={{marginBottom: 'auto'}} />
          <BannerTextWrapper style={{display: 'inline'}}>
            <BannerBoldText>
              You haven't logged any "{artifactTypeName}" artifacts yet!{' '}
            </BannerBoldText>
            You can always link versions later.
          </BannerTextWrapper>
        </BannerFlexWrapper>
      </S.BannerWithMargin>
      <S.Panel>
        <S.Header>How to log an artifact of type "{artifactTypeName}"</S.Header>
        <ol>
          <li>
            Register a {artifactTypeName} in the registry without versions, and
            set the type to "{artifactTypeName}".
          </li>
          <li>
            After achieving satisfactory results from your experiment, utilize
            the following code in your script to log a {artifactTypeName} as an
            artifact of type "{artifactTypeName}".
            <S.CodeSnippet>
              <S.CodeSnippetHeader>
                <Button
                  variant="ghost"
                  size="small"
                  icon={
                    isLogArtifactCodeExpanded ? 'chevron-down' : 'chevron-next'
                  }
                  onClick={onToggleArtifactModelCodeExpanded}
                  style={{marginRight: '8px'}}
                />
                Code snippet
                <div
                  style={{
                    marginLeft: 'auto',
                  }}>
                  <Button
                    icon="copy"
                    variant="ghost"
                    size="small"
                    onClick={onCopyLogArtifactClick}
                    tooltip="Copy to clipboard"
                    style={{
                      backgroundColor: `${hexToRGB(MOON_950, 0.05)}`,
                    }}
                  />
                </div>
              </S.CodeSnippetHeader>
              {isLogArtifactCodeExpanded && (
                <S.Code>{getLogModelCodeSnippet(artifactTypeName)}</S.Code>
              )}
            </S.CodeSnippet>
          </li>
          <li>
            Once you've logged a "{artifactTypeName}" artifact, you can
            conveniently link it to a collection of type "{artifactTypeName}",
            creating a new version. This linking process can be performed either
            from the registry by clicking on 'Link a version' on the collection,
            or through the project artifact section itself.
          </li>
        </ol>
      </S.Panel>
    </>
  );
};
